/**
 * 按钮「点击loading」：拦住整页 Loading，等接口（含嵌套请求）空闲后再结束。
 *
 * 动作脚本常会先跑完同步逻辑（取数、校验），真正的请求稍后才发出。
 * 若函数一返回就把 pending=0 当成结束，会在请求发出前关掉 loading。
 *
 * 异常流程兜底：动作结束（含抛错）后 5 秒仍未关闭则强制结束，
 * 不会从点击时刻起算，避免打断仍在进行的正常请求。
 */
import { nextTick } from 'vue';
const REQUEST_START_GRACE_MS = 1000;
const REQUEST_IDLE_MS = 300;
const FORCE_CLOSE_MS = 5000;

export function startSuppressGlobalLoading() {
  const appObj = (window as any).appObj || {};
  const goingUtils = (window as any).GoingUtils || {};
  const origAppShow = appObj.showLoading;
  const origAppHide = appObj.hideLoading;
  const origGoingShow = goingUtils.showLoading;
  const origGoingClose = goingUtils.closeLoading;
  let pending = 0;
  let seenRequest = false;
  let actionDone = false;
  let settled = false;
  let waitTimer: any = null;
  let forceTimer: any = null;
  let resolveIdle: () => void = () => {};
  const idlePromise = new Promise<void>((resolve) => {
    resolveIdle = resolve;
  });

  const restore = () => {
    if (origAppShow) {
      appObj.showLoading = origAppShow;
    }
    if (origAppHide) {
      appObj.hideLoading = origAppHide;
    }
    if (origGoingShow) {
      goingUtils.showLoading = origGoingShow;
    }
    if (origGoingClose) {
      goingUtils.closeLoading = origGoingClose;
    }
  };

  const clearWaitTimer = () => {
    if (waitTimer) {
      clearTimeout(waitTimer);
      waitTimer = null;
    }
  };

  const clearForceTimer = () => {
    if (forceTimer) {
      clearTimeout(forceTimer);
      forceTimer = null;
    }
  };

  const settle = () => {
    if (settled) {
      return;
    }
    settled = true;
    clearWaitTimer();
    clearForceTimer();
    restore();
    resolveIdle();
  };

  const waitThenSettle = (delay: number) => {
    clearWaitTimer();
    waitTimer = setTimeout(() => {
      if (actionDone && pending <= 0) {
        settle();
      }
    }, delay);
  };

  const onShow = () => {
    seenRequest = true;
    pending += 1;
    clearWaitTimer();
  };
  const onHide = () => {
    pending = Math.max(0, pending - 1);
    if (actionDone && pending <= 0) {
      waitThenSettle(REQUEST_IDLE_MS);
    }
  };

  if (typeof origAppShow === 'function') {
    appObj.showLoading = onShow;
  }
  if (typeof origAppHide === 'function') {
    appObj.hideLoading = onHide;
  }
  if (typeof origGoingShow === 'function') {
    goingUtils.showLoading = onShow;
  }
  if (typeof origGoingClose === 'function') {
    goingUtils.closeLoading = onHide;
  }

  return {
    markActionDone() {
      actionDone = true;
      if (!forceTimer) {
        forceTimer = setTimeout(() => {
          settle();
        }, FORCE_CLOSE_MS);
      }
      if (pending > 0) {
        return;
      }
      // 函数已返回但请求可能还没发出：多等一会，避免被提前结束
      waitThenSettle(seenRequest ? REQUEST_IDLE_MS : REQUEST_START_GRACE_MS);
    },
    idlePromise,
  };
}

export async function runWithBtnActionLoading({
  enabled,
  loadingRef,
  run,
}: {
  enabled: boolean;
  loadingRef: { value: boolean };
  run: (waitAsync: boolean) => any;
}) {
  if (enabled) {
    loadingRef.value = true;
  }
  const loadingSession = enabled ? startSuppressGlobalLoading() : null;
  try {
    const eventRes = run(enabled);
    if (enabled) {
      await nextTick();
      await eventRes;
    }
  } catch (e) {
    // 异常由动作结束后的 5 秒兜底关闭
  } finally {
    if (loadingSession) {
      loadingSession.markActionDone();
      await loadingSession.idlePromise;
      loadingRef.value = false;
    }
  }
}
